import React, { useMemo, useState } from 'react';
import {
  DEFAULT_STARTUP_CALCULATOR_INPUTS,
  STARTUP_CALCULATOR_FORMULA_STEPS,
  calculateStartupCalculatorMetrics,
  formatMoney,
  formatPercent,
  formatQty,
  getInsightState,
} from './startupCalculatorLogic';
import StartupCalculatorNumberField from './StartupCalculatorNumberField';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './StartupCalculator.css';

const CalcRow = ({ label, value, tone = 'default', valueTone }) => {
  const resolvedValueTone = valueTone || tone;

  return (
    <div
      className={`sc-calc-row${tone === 'neutral' ? ' sc-calc-row--neutral' : ''}`}
    >
      <span className="sc-calc-label">{label}</span>
      <span
        className={`sc-calc-val${
          resolvedValueTone === 'neg' ? ' sc-calc-val--neg' : ''
        }`}
      >
        {value}
      </span>
    </div>
  );
};

const StartupCalculator = () => {
  const [inputs, setInputs] = useState(DEFAULT_STARTUP_CALCULATOR_INPUTS);
  const locale = 'ru-RU';
  const pcsUnit = 'шт';

  const metrics = useMemo(() => calculateStartupCalculatorMetrics(inputs), [inputs]);
  const insight = useMemo(() => getInsightState(metrics), [metrics]);

  const insightMessage = useMemo(
    () => formatTrustedMessage(insight.message, insight.vars || {}),
    [insight],
  );

  const handleChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const formatPayback = (months) =>
    Number.isFinite(months) ? `${months.toFixed(1)} ${'мес'}` : '—';

  const salesNeededDisplay = Number.isFinite(metrics.salesNeeded)
    ? formatQty(metrics.salesNeeded, pcsUnit, locale)
    : '∞';

  const bepSalesDisplay = Number.isFinite(metrics.bepSales)
    ? formatQty(metrics.bepSales, pcsUnit, locale)
    : '∞';

  return (
    <div className="sc-calculator">
      <div className="sc-legend">
        <div className="sc-legend-item">
          <span className="sc-legend-dot sc-legend-dot--input" />
          {'Вводите вы'}
        </div>
        <div className="sc-legend-item">
          <span className="sc-legend-dot sc-legend-dot--calc" />
          {'Считается автоматически'}
        </div>
      </div>

      <div className="sc-layout">
        <section className="sc-panel">
          <h2 className="sc-panel-title">{'Ваша цель'}</h2>
          <StartupCalculatorNumberField
            label={'Желаемая чистая прибыль в месяц'}
            unit={'₸'}
            note={'Сколько хотите получать на руки после всех расходов и налогов'}
            value={inputs.goal}
            min={0}
            onChange={(value) => handleChange('goal', value)}
          />
          <StartupCalculatorNumberField
            label={'Средний чек (или цена одной продажи)'}
            unit={'₸'}
            note={'Сколько платит один клиент за одну покупку / услугу'}
            value={inputs.price}
            min={1}
            onChange={(value) => handleChange('price', value)}
          />
          <StartupCalculatorNumberField
            label={'Маржинальность (% прибыли в чеке)'}
            subLabel={'для услуг ~60–90%'}
            unit={'%'}
            note={'Доля чека, которая остаётся после переменных расходов (себестоимость, расходники, процент мастеру)'}
            value={inputs.margin}
            min={1}
            max={100}
            onChange={(value) => handleChange('margin', value)}
          />
          <div className="sc-sep" />
          <CalcRow label={'Валовая прибыль с 1 продажи'} value={formatMoney(metrics.grossPer, locale)} tone="neutral" />
        </section>

        <section className="sc-panel">
          <h2 className="sc-panel-title">{'Переменные расходы на продажу'}</h2>
          <p className="sc-panel-intro">{'Расходы, которые возникают только при каждой продаже. Для чистых услуг без себестоимости — ставьте 0.'}</p>
          <StartupCalculatorNumberField
            label={'Себестоимость'}
            subLabel={'на 1 продажу'}
            unit={'₸'}
            note={'Материалы, товар, расходные материалы на одну продажу (учтены в марже, для справки)'}
            value={inputs.cogs}
            min={0}
            onChange={(value) => handleChange('cogs', value)}
          />
          <StartupCalculatorNumberField
            label={'Реклама на 1 продажу (CPL / CR)'}
            unit={'₸'}
            note={'Рекламный бюджет ÷ количество продаж из рекламы. Если реклама не запущена — 0'}
            value={inputs.adsPer}
            min={0}
            onChange={(value) => handleChange('adsPer', value)}
          />
          <StartupCalculatorNumberField
            label={'Налоги и прочие расходы'}
            subLabel={'% от выручки'}
            unit={'%'}
            note={'УСН «Доходы» — 6%. Если не зарегистрированы — уточните у бухгалтера'}
            value={inputs.tax}
            min={0}
            max={50}
            step={0.5}
            onChange={(value) => handleChange('tax', value)}
          />
          <StartupCalculatorNumberField
            label={'Комиссия банка / эквайринг'}
            unit={'%'}
            value={inputs.bank}
            min={0}
            max={10}
            step={0.5}
            onChange={(value) => handleChange('bank', value)}
          />
          <div className="sc-sep" />
          <CalcRow label={'Итого переменных на 1 продажу'} value={formatMoney(metrics.varPer, locale)} tone="neutral" />
          <CalcRow
            label={'Реальная прибыль с 1 продажи'}
            value={formatMoney(metrics.realPer, locale)}
            valueTone={metrics.realPer < 0 ? 'neg' : 'default'}
          />
        </section>
      </div>

      <section className="sc-panel sc-panel--fixed">
        <h2 className="sc-panel-title">{'Постоянные расходы в месяц'}</h2>
        <div className="sc-layout sc-layout--inner">
          <div>
            <StartupCalculatorNumberField
              label={'Аренда (офис, студия, склад)'}
              unit={'₸'}
              note={'Если работаете из дома — 0'}
              value={inputs.rent}
              min={0}
              onChange={(value) => handleChange('rent', value)}
            />
            <StartupCalculatorNumberField
              label={'Зарплата себе (фикс часть)'}
              unit={'₸'}
              note={'Фиксированная часть, которую вы берёте вне зависимости от выручки'}
              value={inputs.salarySelf}
              min={0}
              onChange={(value) => handleChange('salarySelf', value)}
            />
            <StartupCalculatorNumberField
              label={'Зарплата сотрудникам (фикс)'}
              unit={'₸'}
              value={inputs.salaryStaff}
              min={0}
              onChange={(value) => handleChange('salaryStaff', value)}
            />
          </div>
          <div>
            <StartupCalculatorNumberField
              label={'Маркетинговые расходы'}
              unit={'₸'}
              note={'Постоянный рекламный бюджет, не зависящий от продаж'}
              value={inputs.marketing}
              min={0}
              onChange={(value) => handleChange('marketing', value)}
            />
            <StartupCalculatorNumberField
              label={'Сервисы, подписки, ПО'}
              unit={'₸'}
              value={inputs.services}
              min={0}
              onChange={(value) => handleChange('services', value)}
            />
            <StartupCalculatorNumberField
              label={'Прочие постоянные расходы'}
              unit={'₸'}
              note={'Коммуналка, связь, транспорт, резерв'}
              value={inputs.other}
              min={0}
              onChange={(value) => handleChange('other', value)}
            />
          </div>
        </div>
        <div className="sc-sep" />
        <div className="sc-layout sc-layout--totals">
          <CalcRow label={'Итого постоянных расходов'} value={formatMoney(metrics.fixed, locale)} tone="neutral" />
          <CalcRow label={'Всего расходов в месяц'} value={formatMoney(metrics.totalCosts, locale)} tone="neutral" />
        </div>
      </section>

      <div className="sc-hero-row">
        <article className="sc-hero-card sc-hero-card--dark">
          <div className="sc-hero-label">{'Нужно продаж в месяц'}</div>
          <div className="sc-hero-val">{salesNeededDisplay}</div>
          <div className="sc-hero-sub">
            {Number.isFinite(metrics.salesDaily)
              ? `${metrics.salesDaily.toFixed(1)} ${'продаж в день'}`
              : ''}
          </div>
        </article>
        <article className="sc-hero-card sc-hero-card--green">
          <div className="sc-hero-label">{'Точка безубыточности'}</div>
          <div className="sc-hero-val">{bepSalesDisplay}</div>
          <div className="sc-hero-sub">
            {Number.isFinite(metrics.bepRub)
              ? `${formatMoney(metrics.bepRub, locale)} ${'выручки'}`
              : '—'}
          </div>
        </article>
        <article className="sc-hero-card sc-hero-card--accent">
          <div className="sc-hero-label">{'Мин. чек для безубытка'}</div>
          <div className="sc-hero-val">{formatMoney(metrics.minCheck, locale)}</div>
          <div className="sc-hero-sub">{'при текущих продажах'}</div>
        </article>
      </div>

      <div className="sc-layout-3">
        <article className="sc-mini-card sc-mini-card--green">
          <div className="sc-mini-label">{'Нужная выручка в месяц'}</div>
          <div className="sc-mini-val">{formatMoney(metrics.revenueNeeded, locale)}</div>
          <div className="sc-mini-sub">{'для желаемой прибыли'}</div>
          <p className="sc-mini-explain">{'Прибыль + все расходы. Именно столько должно приходить на счёт.'}</p>
        </article>
        <article className="sc-mini-card sc-mini-card--blue">
          <div className="sc-mini-label">{'Реальная маржинальность'}</div>
          <div className="sc-mini-val">{formatPercent(metrics.realMargin)}</div>
          <div className="sc-mini-sub">{'после всех переменных'}</div>
          <p className="sc-mini-explain">{'Доля, которая остаётся с каждого чека после себестоимости, рекламы, налогов и комиссий.'}</p>
        </article>
        <article className="sc-mini-card sc-mini-card--purple">
          <div className="sc-mini-label">{'Запас прочности'}</div>
          <div className="sc-mini-val">{formatPercent(metrics.safety)}</div>
          <div className="sc-mini-sub">
            {Number.isFinite(metrics.bepSales)
              ? `сверх ТБУ ${metrics.bepSales}`
              : '—'}
          </div>
          <p className="sc-mini-explain">{'Насколько продажи превышают точку безубыточности. Чем выше — тем устойчивее бизнес.'}</p>
        </article>
      </div>

      <section className="sc-panel sc-panel--fixed">
        <h2 className="sc-panel-title">{'Что будет при разном среднем чеке'}</h2>
        <p className="sc-panel-intro">{'При вашем уровне постоянных расходов и переменных — сколько продаж нужно при разных ценах'}</p>
        <div className="sc-table-wrap">
          <table className="sc-table">
            <thead>
              <tr>
                <th>{'Средний чек'}</th>
                <th>{'Прибыль с продажи'}</th>
                <th>{'Точка безуб. (шт)'}</th>
                <th>{'Для цели (шт)'}</th>
                <th>{'Нужная выручка'}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.scenarios.map((row) => (
                <tr key={row.price} className={row.isCurrent ? 'sc-table-row--current' : ''}>
                  <td className="sc-table-name">
                    {formatMoney(row.price, locale)}
                    {row.isCurrent ? ' ← ваш' : ''}
                  </td>
                  <td className={`sc-tone-${row.tone}`}>{formatMoney(row.profitPer, locale)}</td>
                  <td className={row.bepQty ? '' : 'sc-tone-neg'}>
                    {row.bepQty ? formatQty(row.bepQty, pcsUnit, locale) : '∞'}
                  </td>
                  <td className={row.goalQty ? '' : 'sc-tone-neg'}>
                    {row.goalQty ? formatQty(row.goalQty, pcsUnit, locale) : '∞'}
                  </td>
                  <td>{row.revenue ? formatMoney(row.revenue, locale) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sc-panel sc-panel--fixed">
        <h2 className="sc-panel-title">{'Срок окупаемости стартовых вложений'}</h2>
        <div className="sc-layout sc-layout--goal">
          <StartupCalculatorNumberField
            label={'Стартовые вложения (разовые)'}
            unit={'₸'}
            note={'Оборудование, ремонт, разработка, регистрация — всё, что тратите один раз на старте'}
            value={inputs.invest}
            min={0}
            onChange={(value) => handleChange('invest', value)}
          />
          <div className="sc-goal-calc">
            <CalcRow label={'Окупаемость при целевой прибыли'} value={formatPayback(metrics.payback)} />
            <CalcRow label={'Окупаемость при прибыли ×1.5'} value={formatPayback(metrics.paybackOpt)} tone="neutral" />
          </div>
        </div>
      </section>

      <div
        className={`sc-insight sc-insight--${insight.variant}`}
        dangerouslySetInnerHTML={{ __html: insightMessage }}
      />

      <details className="sc-formula-details">
        <summary className="sc-formula-summary">{'Как считается — шаг за шагом'}</summary>
        <section className="sc-formula-panel">
          {STARTUP_CALCULATOR_FORMULA_STEPS.map((step) => (
            <article key={step.marker} className="sc-formula-step">
              <div className="sc-formula-step-num">{step.marker}</div>
              <div>
                <div className="sc-formula-step-title">{step.title}</div>
                <div className="sc-formula-step-formula">{step.formula}</div>
                <div
                  className="sc-formula-step-example"
                  dangerouslySetInnerHTML={{
                    __html: step.example,
                  }}
                />
              </div>
            </article>
          ))}
        </section>
      </details>
    </div>
  );
};

export default StartupCalculator;
